import React, { useEffect } from 'react';
import { useBMP } from '../context/BMPContext';
import useMarketFarms from '../hooks/useMarketFarms';
import useMarketQuotas from '../hooks/useMarketQuotas';
import useMarketBids from '../hooks/useMarketBids';
import useMarketTransactions from '../hooks/useMarketTransactions';
import FarmCard from '../cards/FarmCard';
import BidCard from '../cards/BidCard';
import TransactionCard from '../cards/TransactionCard';
import QuotaCard from '../cards/QuotaCard';
import FarmFormModal from '../forms/FarmFormModal';
import QuotaFormModal from '../forms/QuotaFormModal';
import { useTranslation } from 'react-i18next';
import './DashboardView.css';

export default function DashboardView() {
  const { t } = useTranslation();
  const { bmpView, isProducer } = useBMP();
  const farmsHook = useMarketFarms();
  const quotasHook = useMarketQuotas();
  const bidsHook = useMarketBids();
  const txHook = useMarketTransactions();

  const [showFarmForm, setShowFarmForm] = React.useState(false);
  const [editFarm, setEditFarm] = React.useState(null);
  const [showQuotaForm, setShowQuotaForm] = React.useState(false);

  const fetchFarms = farmsHook.fetchFarms;
  const fetchQuotas = quotasHook.fetchQuotas;
  const fetchBids = bidsHook.fetchBids;
  const fetchTx = txHook.fetchTransactions;

  useEffect(() => {
    if (bmpView === 'farms') fetchFarms();
    if (bmpView === 'my_quotas') fetchQuotas({ mine: '1' });
    if (bmpView === 'received_bids' || bmpView === 'my_bids') fetchBids();
    if (bmpView === 'transactions') fetchTx();
  }, [bmpView, fetchFarms, fetchQuotas, fetchBids, fetchTx]);

  // ── FARMS ──
  if (bmpView === 'farms') {
    return (
      <div className="dash">
        <div className="bmp-section-header">
          <h2>{t('bmp_my_farms')}</h2>
          <button className="bmp-btn bmp-btn-primary" onClick={() => { setEditFarm(null); setShowFarmForm(true); }}>
            + {t('bmp_add_farm')}
          </button>
        </div>

        {showFarmForm && (
          <FarmFormModal
            farm={editFarm}
            onClose={() => { setShowFarmForm(false); setEditFarm(null); }}
            onSave={async (data) => {
              if (editFarm) await farmsHook.updateFarm(editFarm.id, data);
              else await farmsHook.createFarm(data);
              setShowFarmForm(false);
              setEditFarm(null);
            }}
            saving={farmsHook.saving}
          />
        )}

        {farmsHook.farms.length === 0 ? (
          <div className="bmp-empty">{t('bmp_no_farms')}</div>
        ) : (
          <div className="dash-grid">
            {farmsHook.farms.map(f => (
              <FarmCard
                key={f.id}
                farm={f}
                onEdit={() => { setEditFarm(f); setShowFarmForm(true); }}
                onDelete={() => { if (window.confirm(t('bmp_confirm_delete_farm'))) farmsHook.deleteFarm(f.id); }}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── MY QUOTAS ──
  if (bmpView === 'my_quotas') {
    return (
      <div className="dash">
        <div className="bmp-section-header">
          <h2>{t('bmp_my_quotas')}</h2>
          {farmsHook.farms.length > 0 ? (
            <button className="bmp-btn bmp-btn-primary" onClick={() => { farmsHook.fetchFarms(); setShowQuotaForm(true); }}>
              + {t('bmp_publish_quota')}
            </button>
          ) : (
            <span className="dash-hint">{t('bmp_add_farm_first')}</span>
          )}
        </div>

        {showQuotaForm && (
          <QuotaFormModal
            farms={farmsHook.farms}
            onClose={() => setShowQuotaForm(false)}
            onSave={async (data) => {
              await quotasHook.createQuota(data);
              setShowQuotaForm(false);
            }}
            saving={quotasHook.saving}
          />
        )}

        {quotasHook.quotas.length === 0 ? (
          <div className="bmp-empty">{t('bmp_no_quotas')}</div>
        ) : (
          <div className="dash-grid">
            {quotasHook.quotas.map(q => <QuotaCard key={q.id} quota={q} />)}
          </div>
        )}
      </div>
    );
  }

  // ── BIDS (received or sent) ──
  if (bmpView === 'received_bids' || bmpView === 'my_bids') {
    const handleRespond = async (bidId, action) => {
      const msg = action === 'accept' ? t('mkt_confirm_accept') : t('mkt_confirm_reject');
      if (!window.confirm(msg)) return;
      await bidsHook.respondBid(bidId, action);
      txHook.fetchTransactions();
    };

    return (
      <div className="dash">
        <div className="bmp-section-header">
          <h2>{isProducer ? t('bmp_received_bids') : t('bmp_my_bids')}</h2>
        </div>
        {bidsHook.bids.length === 0 ? (
          <div className="bmp-empty">{t('bmp_no_bids')}</div>
        ) : (
          <div className="dash-list">
            {bidsHook.bids.map(b => (
              <BidCard key={b.id} bid={b} isProducer={isProducer} onRespond={handleRespond} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── TRANSACTIONS ──
  if (bmpView === 'transactions') {
    return (
      <div className="dash">
        <div className="bmp-section-header">
          <h2>{t('bmp_transactions')}</h2>
        </div>
        {txHook.transactions.length === 0 ? (
          <div className="bmp-empty">{t('bmp_no_transactions')}</div>
        ) : (
          <div className="dash-list">
            {txHook.transactions.map(tx => (
              <TransactionCard key={tx.id} tx={tx} isProducer={isProducer} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}
